import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPaymentStatus } from "@/api";
import { useAuth } from "@/auth";
import { PAYMENT } from "@/constants/testIds";
import AsciiDivider from "@/components/AsciiDivider";

const MAX_POLLS = 8;
const POLL_INTERVAL = 1800;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("missing");
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      setTries(attempts);
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid" || data.activated) {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setError(
          typeof e?.response?.data?.detail === "string"
            ? e.response.data.detail
            : "Could not verify payment."
        );
        setStatus("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  return (
    <div className="term-container fade-in" data-testid={PAYMENT.successRoot}>
      <div className="kbd-label">// transaction</div>
      <h1 style={{ margin: "0.2rem 0 1rem", color: "var(--ax-accent)" }}>
        PAYMENT
      </h1>
      <AsciiDivider char="=" />

      <div className="term-card" data-testid={PAYMENT.status}>
        {status === "checking" && (
          <div>
            <span className="muted">$ verifying transaction</span>
            <span className="cursor" />
            <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
              attempt {tries}/{MAX_POLLS}
            </div>
          </div>
        )}
        {status === "paid" && (
          <div>
            <div className="pill active">PAID</div>
            <div style={{ marginTop: 10 }}>
              Access granted. The full dossier archive is now open to you.
            </div>
            <div style={{ marginTop: "0.8rem" }}>
              <button
                data-testid={PAYMENT.goAccount}
                onClick={() => navigate("/")}
              >
                ENTER ARCHIVE →
              </button>
            </div>
          </div>
        )}
        {status === "expired" && (
          <div>
            <div className="pill warn">EXPIRED</div>
            <p className="muted">The checkout session expired without payment.</p>
            <button onClick={() => navigate("/account")}>
              ← BACK TO ACCOUNT
            </button>
          </div>
        )}
        {status === "timeout" && (
          <div>
            <div className="pill warn">PENDING</div>
            <p className="muted">
              We couldn&apos;t confirm in time. Check{" "}
              <span className="glow-dim">/account</span> in a minute.
            </p>
            <button onClick={() => navigate("/account")}>
              GO TO ACCOUNT
            </button>
          </div>
        )}
        {status === "missing" && (
          <div>
            <div className="pill danger">MISSING SESSION</div>
            <p className="muted">No checkout session id supplied.</p>
            <button onClick={() => navigate("/")}>GO HOME</button>
          </div>
        )}
        {status === "error" && (
          <div>
            <div className="pill danger">ERROR</div>
            <p style={{ color: "var(--ax-danger)" }}>{error}</p>
            <button onClick={() => navigate("/account")}>GO TO ACCOUNT</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
